import React, { useState } from 'react';
import { Plus, Trash2, Ticket } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { SkeletonTable } from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/EmptyState';
import InviteCodeCreatedModal from '../components/invite-codes/InviteCodeCreatedModal';
import DeleteInviteCodeModal from '../components/invite-codes/DeleteInviteCodeModal';
import { formatDate } from '../utils/dateUtils';
import { useInviteCodes, useCreateInviteCode } from '../hooks/useInviteCodes';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';

const StatusBadge = ({ usedAt }) => {
    const { t } = useLanguage();
    if (usedAt) {
        return (
            <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-surface-overlay text-text-muted border border-surface-border">
                {t(KEYS.INVITE_CODES_STATUS_USED)}
            </span>
        );
    }
    return (
        <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-status-paid-bg text-status-paid">
            {t(KEYS.INVITE_CODES_STATUS_UNUSED)}
        </span>
    );
};

const InviteCodes = () => {
    const { data: inviteCodes = [], isLoading: loading, error, refetch } = useInviteCodes();
    const createInviteCode = useCreateInviteCode();
    const { t } = useLanguage();

    const [deleteCode, setDeleteCode] = useState(null);
    const [createdPlaintext, setCreatedPlaintext] = useState(null);
    const [generateError, setGenerateError] = useState('');

    const handleGenerate = async () => {
        setGenerateError('');
        try {
            const data = await createInviteCode.mutateAsync();
            setCreatedPlaintext(data.plaintext);
        } catch (error) {
            console.error('Error generating invite code:', error);
            setGenerateError(error.message || 'Failed to generate invite code. Please try again.');
        }
    };

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-text-primary">{t(KEYS.INVITE_CODES_TITLE)}</h1>
                    <p className="text-sm text-text-muted mt-0.5">
                        {t(KEYS.INVITE_CODES_SUBTITLE)}
                    </p>
                </div>
                <Button
                    variant="primary"
                    size="small"
                    onClick={handleGenerate}
                    disabled={createInviteCode.isPending}
                    className="gap-2"
                >
                    <Plus className="w-3.5 h-3.5" />
                    {createInviteCode.isPending ? t(KEYS.INVITE_CODES_GENERATING) : t(KEYS.INVITE_CODES_GENERATE)}
                </Button>
            </div>

            {generateError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {generateError}
                </div>
            )}

            {/* Content */}
            {loading ? (
                <SkeletonTable rows={3} columns={4} />
            ) : error ? (
                <ErrorState message={error?.message || t(KEYS.INVITE_CODES_ERROR_LOAD)} onRetry={refetch} />
            ) : inviteCodes.length > 0 ? (
                <Card padding="none" className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-surface-border">
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVITE_CODES_COL_CODE)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest">
                                        {t(KEYS.INVITE_CODES_COL_STATUS)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden sm:table-cell">
                                        {t(KEYS.INVITE_CODES_COL_USED)}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-[10px] font-medium text-text-muted uppercase tracking-widest hidden md:table-cell">
                                        {t(KEYS.INVITE_CODES_COL_CREATED)}
                                    </th>
                                    <th className="px-4 py-2.5 w-12"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-border">
                                {inviteCodes.map((code, index) => (
                                    <tr
                                        key={code.uid}
                                        className="hover:bg-surface-overlay transition-colors group animate-appear"
                                        style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                        <td className="px-4 py-2.5">
                                            <code className="text-xs font-mono text-text-primary">
                                                {code.uid}
                                            </code>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <StatusBadge usedAt={code.used_at} />
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden sm:table-cell">
                                            {code.used_at ? formatDate(code.used_at) : '—'}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs font-mono text-text-muted hidden md:table-cell">
                                            {formatDate(code.created_at)}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex justify-end">
                                                <button
                                                    onClick={() => setDeleteCode(code)}
                                                    className="p-2.5 sm:p-1.5 text-text-muted hover:text-status-overdue rounded transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <Card className="animate-appear">
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                        <Ticket className="w-8 h-8 text-text-muted mb-3" />
                        <p className="text-sm font-medium text-text-primary mb-1">{t(KEYS.INVITE_CODES_EMPTY_TITLE)}</p>
                        <p className="text-xs text-text-muted mb-4">
                            {t(KEYS.INVITE_CODES_EMPTY_DESCRIPTION)}
                        </p>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={handleGenerate}
                            disabled={createInviteCode.isPending}
                            className="gap-2"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            {createInviteCode.isPending ? t(KEYS.INVITE_CODES_GENERATING) : t(KEYS.INVITE_CODES_GENERATE)}
                        </Button>
                    </div>
                </Card>
            )}

            {/* Modals */}
            <InviteCodeCreatedModal
                isOpen={!!createdPlaintext}
                onClose={() => setCreatedPlaintext(null)}
                plaintext={createdPlaintext}
            />
            <DeleteInviteCodeModal
                isOpen={!!deleteCode}
                onClose={() => setDeleteCode(null)}
                inviteCode={deleteCode}
            />
        </div>
    );
};

export default InviteCodes;
